import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { planningApi, projectApi, settingsApi, aiApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import {
  Plus, Funnel, Eye, Trash, PencilSimple, CheckCircle, XCircle,
  WarningCircle, CalendarBlank, ChartPie, ListBullets, TreeStructure,
  ChartBar as ChartBarIcon, Export, Clock, Flag, CaretDown, CaretRight,
  ArrowRight, Stamp, WarningDiamond, Timer, UploadSimple, Check, X,
  Link as LinkIcon, ArrowBendDownRight, Sparkle, CircleNotch, Brain,
  WarningOctagon
} from '@phosphor-icons/react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const STATUS_COLORS = {
  'Not Started': { bg: '#F3F4F6', text: '#374151', fill: '#9CA3AF', bar: '#94A3B8' },
  'In Progress': { bg: '#DBEAFE', text: '#1E40AF', fill: '#3B82F6', bar: '#3B82F6' },
  'Completed': { bg: '#D1FAE5', text: '#065F46', fill: '#10B981', bar: '#10B981' },
  'On Hold': { bg: '#FEF3C7', text: '#92400E', fill: '#F59E0B', bar: '#F59E0B' },
  'Delayed': { bg: '#FED7AA', text: '#9A3412', fill: '#F97316', bar: '#F97316' },
  'Cancelled': { bg: '#FEE2E2', text: '#991B1B', fill: '#EF4444', bar: '#EF4444' },
};
const PRIORITY_COLORS = { Low: '#6B7280', Medium: '#3B82F6', High: '#F59E0B', Critical: '#DC2626' };
const APPROVAL_COLORS = { Pending: '#F59E0B', Approved: '#10B981', Rejected: '#EF4444', 'Revision Required': '#F97316' };
const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];
const ALL_STATUSES = ['Not Started', 'In Progress', 'Completed', 'On Hold', 'Delayed', 'Cancelled'];
const ALL_PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

const MAIN_TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: ChartPie },
  { key: 'schedule', label: 'Activity Schedule', icon: ListBullets },
  { key: 'gantt', label: 'Gantt Chart', icon: ChartBarIcon },
  { key: 'categories', label: 'Categories', icon: TreeStructure },
];

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS['Not Started'];
  return (
    <span data-testid={`status-badge-${status?.replace(/\s+/g,'-').toLowerCase()}`}
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={{ backgroundColor: c.bg, color: c.text }}>
      {status}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const color = PRIORITY_COLORS[priority] || '#6B7280';
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold"
      style={{ color, borderColor: color, border: '1px solid' }}>
      {priority}
    </span>
  );
}

function ApprovalBadge({ status }) {
  const color = APPROVAL_COLORS[status] || '#6B7280';
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold" style={{ color }}>
      {status === 'Approved' && <CheckCircle weight="fill" size={12} />}
      {status === 'Rejected' && <XCircle weight="fill" size={12} />}
      {status === 'Pending' && <Clock weight="fill" size={12} />}
      {status}
    </span>
  );
}

// ============ INLINE EDIT CELL ============

function InlineSelect({ value, options, onSave, renderValue }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const ref = useRef(null);

  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  if (!editing) {
    return (
      <div className="cursor-pointer group relative" onClick={() => { setVal(value); setEditing(true); }}
        title="Click to edit">
        {renderValue ? renderValue(value) : value}
        <PencilSimple size={10} className="absolute -right-3 top-0 opacity-0 group-hover:opacity-60 text-[var(--text-muted)]" />
      </div>
    );
  }
  return (
    <select ref={ref} value={val} onChange={e => setVal(e.target.value)}
      onBlur={() => { if (val !== value) onSave(val); setEditing(false); }}
      onKeyDown={e => { if (e.key === 'Escape') setEditing(false); if (e.key === 'Enter') { if (val !== value) onSave(val); setEditing(false); } }}
      className="text-xs bg-[var(--surface)] border border-blue-500 rounded px-1 py-0.5 text-[var(--text-primary)] outline-none w-full">
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function InlineInput({ value, type = 'text', onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const ref = useRef(null);

  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  if (!editing) {
    return (
      <div className="cursor-pointer group relative" onClick={() => { setVal(value); setEditing(true); }}
        title="Click to edit">
        <span className="text-xs text-[var(--text-secondary)]">{value || '-'}</span>
        <PencilSimple size={10} className="absolute -right-3 top-0 opacity-0 group-hover:opacity-60 text-[var(--text-muted)]" />
      </div>
    );
  }
  return (
    <input ref={ref} type={type} value={val} onChange={e => setVal(e.target.value)}
      onBlur={() => { if (val !== value) onSave(val); setEditing(false); }}
      onKeyDown={e => { if (e.key === 'Escape') setEditing(false); if (e.key === 'Enter') { if (val !== value) onSave(val); setEditing(false); } }}
      className="text-xs bg-[var(--surface)] border border-blue-500 rounded px-1 py-0.5 text-[var(--text-primary)] outline-none w-full" />
  );
}

function InlineProgress({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  if (!editing) {
    return (
      <div className="flex items-center gap-2 cursor-pointer group" onClick={() => { setVal(value); setEditing(true); }}
        title="Click to edit">
        <div className="w-16 h-1.5 bg-[var(--border)] rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{
            width: `${value || 0}%`,
            backgroundColor: value >= 100 ? '#10B981' : value >= 50 ? '#3B82F6' : '#F59E0B',
          }} />
        </div>
        <span className="text-xs text-[var(--text-muted)]">{value || 0}%</span>
        <PencilSimple size={10} className="opacity-0 group-hover:opacity-60 text-[var(--text-muted)]" />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <input type="range" min="0" max="100" step="5" value={val}
        onChange={e => setVal(parseInt(e.target.value))}
        className="w-16 accent-blue-600" />
      <span className="text-xs font-semibold w-8">{val}%</span>
      <button onClick={() => { onSave(val); setEditing(false); }} className="text-green-500"><Check size={14} /></button>
      <button onClick={() => setEditing(false)} className="text-red-500"><X size={14} /></button>
    </div>
  );
}

// ============ GANTT CHART WITH DRAG + DEPENDENCIES ============

function GanttChart({ activities, startDate, endDate, onUpdateDates, dependencies }) {
  const ganttRef = useRef(null);
  const [dragging, setDragging] = useState(null); // { actId, mode: 'move'|'resize', startX, origStart, origEnd }

  const { days, totalDays, start } = useMemo(() => {
    if (!startDate || !endDate) return { days: [], totalDays: 0, start: null };
    const s = new Date(startDate);
    const e = new Date(endDate);
    const total = Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1;
    const d = [];
    for (let i = 0; i < total; i++) {
      const dt = new Date(s);
      dt.setDate(dt.getDate() + i);
      d.push(dt);
    }
    return { days: d, totalDays: total, start: s };
  }, [startDate, endDate]);

  const dayWidth = Math.max(28, 900 / totalDays);
  const rowHeight = 40;

  const getBarPosition = useCallback((ps, pe) => {
    if (!ps || !pe || !start) return null;
    const psDate = new Date(ps);
    const peDate = new Date(pe);
    const leftDays = Math.max(0, (psDate - start) / (1000 * 60 * 60 * 24));
    const widthDays = Math.max(1, (peDate - psDate) / (1000 * 60 * 60 * 24) + 1);
    return { left: leftDays * dayWidth, width: widthDays * dayWidth };
  }, [start, dayWidth]);

  const dateFromOffset = useCallback((offsetX) => {
    if (!start) return '';
    const dayOffset = Math.round(offsetX / dayWidth);
    const d = new Date(start);
    d.setDate(d.getDate() + dayOffset);
    return d.toISOString().split('T')[0];
  }, [start, dayWidth]);

  // Drag handlers
  const handleMouseDown = useCallback((e, actId, mode, bar) => {
    e.preventDefault();
    const act = activities.find(a => a.id === actId);
    if (!act) return;
    setDragging({
      actId, mode,
      startX: e.clientX,
      barLeft: bar.left,
      barWidth: bar.width,
      origStart: act.planned_start,
      origEnd: act.planned_end,
    });
  }, [activities]);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const dx = e.clientX - dragging.startX;
      setDragging(prev => ({ ...prev, dx }));
    };
    const handleUp = (e) => {
      const dx = e.clientX - dragging.startX;
      if (Math.abs(dx) > 5) {
        if (dragging.mode === 'move') {
          const newLeft = dragging.barLeft + dx;
          const newStart = dateFromOffset(newLeft);
          const newEnd = dateFromOffset(newLeft + dragging.barWidth - dayWidth);
          onUpdateDates(dragging.actId, newStart, newEnd);
        } else if (dragging.mode === 'resize') {
          const newWidth = Math.max(dayWidth, dragging.barWidth + dx);
          const newEnd = dateFromOffset(dragging.barLeft + newWidth - dayWidth);
          onUpdateDates(dragging.actId, dragging.origStart, newEnd);
        }
      }
      setDragging(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
  }, [dragging, dateFromOffset, dayWidth, onUpdateDates]);

  if (!activities.length || totalDays < 1) {
    return (
      <div className="text-center py-12 text-[var(--text-secondary)]">
        No activities to display. Add activities with dates to see the Gantt chart.
      </div>
    );
  }

  const months = [];
  let currentMonth = null;
  days.forEach((d, i) => {
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key !== currentMonth) {
      months.push({ label: d.toLocaleString('default', { month: 'short', year: 'numeric' }), start: i, span: 1 });
      currentMonth = key;
    } else { months[months.length - 1].span++; }
  });

  const todayOffset = (() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = (today - start) / (1000 * 60 * 60 * 24);
    if (diff < 0 || diff > totalDays) return null;
    return diff * dayWidth;
  })();

  // Build activity index for dependency arrows
  const actIndex = {};
  activities.forEach((a, i) => { actIndex[a.id] = i; });

  // Dependency arrows (SVG)
  const depArrows = [];
  (dependencies || []).forEach(dep => {
    const fromIdx = actIndex[dep.from];
    const toIdx = actIndex[dep.to];
    if (fromIdx === undefined || toIdx === undefined) return;
    const fromAct = activities[fromIdx];
    const toAct = activities[toIdx];
    const fromBar = getBarPosition(fromAct.planned_start, fromAct.planned_end);
    const toBar = getBarPosition(toAct.planned_start, toAct.planned_end);
    if (!fromBar || !toBar) return;
    const x1 = 260 + fromBar.left + fromBar.width;
    const y1 = fromIdx * rowHeight + rowHeight / 2;
    const x2 = 260 + toBar.left;
    const y2 = toIdx * rowHeight + rowHeight / 2;
    depArrows.push({ key: `${dep.from}-${dep.to}`, x1, y1, x2, y2 });
  });

  const headerHeight = 52;

  return (
    <div ref={ganttRef} className="overflow-x-auto border border-[var(--border)] rounded-lg" data-testid="gantt-chart">
      <div style={{ minWidth: totalDays * dayWidth + 260 }} className="relative">
        {/* Header */}
        <div className="flex sticky top-0 z-10 bg-[var(--surface)]">
          <div className="w-[260px] min-w-[260px] border-r border-b border-[var(--border)] px-3 py-2 text-xs font-semibold text-[var(--text-secondary)] flex items-center">
            Activity
          </div>
          <div className="flex-1">
            <div className="flex border-b border-[var(--border)]">
              {months.map((m, i) => (
                <div key={i} style={{ width: m.span * dayWidth }}
                  className="text-xs font-semibold text-center py-1 text-[var(--text-secondary)] border-r border-[var(--border)]">
                  {m.label}
                </div>
              ))}
            </div>
            <div className="flex border-b border-[var(--border)]">
              {days.map((d, i) => {
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <div key={i} style={{ width: dayWidth }}
                    className={`text-[10px] text-center py-1 border-r border-[var(--border)] ${isWeekend ? 'bg-[var(--surface-hover)] text-[var(--text-muted)]' : 'text-[var(--text-secondary)]'}`}>
                    {d.getDate()}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Body rows */}
        <div className="relative">
          {/* SVG overlay for dependency arrows */}
          {depArrows.length > 0 && (
            <svg className="absolute inset-0 pointer-events-none z-[6]"
              style={{ width: totalDays * dayWidth + 260, height: activities.length * rowHeight }}>
              <defs>
                <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#F59E0B" />
                </marker>
              </defs>
              {depArrows.map(a => {
                const midX = (a.x1 + a.x2) / 2;
                return (
                  <path key={a.key}
                    d={`M ${a.x1} ${a.y1} C ${midX} ${a.y1}, ${midX} ${a.y2}, ${a.x2} ${a.y2}`}
                    fill="none" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="4 2"
                    markerEnd="url(#arrowhead)" opacity="0.7" />
                );
              })}
            </svg>
          )}

          {activities.map((act, rowIdx) => {
            const bar = getBarPosition(act.planned_start, act.planned_end);
            const actualBar = (act.actual_start && act.actual_end) ? getBarPosition(act.actual_start, act.actual_end) : null;
            const sc = STATUS_COLORS[act.status] || STATUS_COLORS['Not Started'];

            // Apply drag offset
            let renderBar = bar;
            if (dragging && dragging.actId === act.id && bar) {
              const dx = dragging.dx || 0;
              if (dragging.mode === 'move') {
                renderBar = { left: bar.left + dx, width: bar.width };
              } else if (dragging.mode === 'resize') {
                renderBar = { left: bar.left, width: Math.max(dayWidth, bar.width + dx) };
              }
            }

            return (
              <div key={act.id} className="flex" style={{ height: rowHeight }} data-testid={`gantt-row-${act.id}`}>
                <div className="w-[260px] min-w-[260px] border-r border-b border-[var(--border)] px-3 flex items-center gap-2 truncate">
                  {act.milestone && <Flag weight="fill" size={12} className="text-yellow-500 flex-shrink-0" />}
                  <span className="text-xs truncate text-[var(--text-primary)]">{act.title}</span>
                  <StatusBadge status={act.status} />
                </div>
                <div className="flex-1 relative border-b border-[var(--border)]">
                  <div className="absolute inset-0 flex">
                    {days.map((d, i) => {
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      return <div key={i} style={{ width: dayWidth }} className={`border-r border-[var(--border)] ${isWeekend ? 'bg-[var(--surface-hover)]/30' : ''}`} />;
                    })}
                  </div>
                  {todayOffset !== null && (
                    <div className="absolute top-0 bottom-0 w-px bg-red-500 z-[5]" style={{ left: todayOffset }} />
                  )}
                  {/* Planned bar — draggable */}
                  {renderBar && (
                    <div
                      className={`absolute z-[4] rounded ${dragging?.actId === act.id ? 'opacity-70' : 'cursor-grab hover:brightness-110'}`}
                      style={{
                        left: renderBar.left, width: Math.max(renderBar.width, 4), top: 6,
                        height: actualBar ? 14 : 26,
                        backgroundColor: sc.bar, opacity: 0.85,
                      }}
                      onMouseDown={e => handleMouseDown(e, act.id, 'move', bar)}
                      title={`Planned: ${act.planned_start} → ${act.planned_end}\nDrag to reschedule`}>
                      {renderBar.width > 50 && (
                        <span className="text-[9px] text-white px-1 truncate block leading-[14px]">
                          {act.progress || 0}%
                        </span>
                      )}
                      {/* Resize handle on right edge */}
                      <div className="absolute right-0 top-0 bottom-0 w-2 cursor-e-resize hover:bg-white/30 rounded-r"
                        onMouseDown={e => { e.stopPropagation(); handleMouseDown(e, act.id, 'resize', bar); }} />
                    </div>
                  )}
                  {actualBar && (
                    <div className="absolute z-[3] rounded"
                      style={{
                        left: actualBar.left, width: Math.max(actualBar.width, 4), top: 22,
                        height: 12, backgroundColor: sc.bar, opacity: 0.45,
                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.2) 3px, rgba(255,255,255,0.2) 6px)',
                      }}
                      title={`Actual: ${act.actual_start} → ${act.actual_end}`} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============

export default function PlanningPage() {
  const [mainTab, setMainTab] = useState('dashboard');
  const [activities, setActivities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], priorities: [], approval_statuses: [] });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [filterProject, setFilterProject] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubCategory, setFilterSubCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterApproval, setFilterApproval] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDepsModal, setShowDepsModal] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);

  const [ganttStart, setGanttStart] = useState('');
  const [ganttEnd, setGanttEnd] = useState('');
  const [companySettings, setCompanySettings] = useState(null);

  useEffect(() => {
    Promise.all([
      projectApi.list(),
      planningApi.getMeta(),
      planningApi.listCategories(),
      settingsApi.get().catch(() => ({ data: {} })),
      aiApi.getSettings().catch(() => ({ data: { ai_enabled: false } })),
    ]).then(([pRes, mRes, cRes, sRes, aiRes]) => {
      setProjects(pRes.data || []);
      setMeta(mRes.data);
      setCategories(cRes.data || []);
      setCompanySettings(sRes.data);
      setAiEnabled(aiRes.data?.ai_enabled || false);
    }).finally(() => setLoading(false));
  }, []);

  const loadActivities = useCallback(async () => {
    const params = {};
    if (filterProject) params.project_id = filterProject;
    if (filterCategory) params.category = filterCategory;
    if (filterSubCategory) params.sub_category = filterSubCategory;
    if (filterStatus) params.status = filterStatus;
    if (filterPriority) params.priority = filterPriority;
    if (filterApproval) params.approval_status = filterApproval;
    if (filterDateFrom) params.start_from = filterDateFrom;
    if (filterDateTo) params.end_to = filterDateTo;
    const res = await planningApi.listActivities(params);
    setActivities(res.data || []);
  }, [filterProject, filterCategory, filterSubCategory, filterStatus, filterPriority, filterApproval, filterDateFrom, filterDateTo]);

  const loadSummary = useCallback(async () => {
    const params = {};
    if (filterProject) params.project_id = filterProject;
    const res = await planningApi.getSummary(params);
    setSummary(res.data);
  }, [filterProject]);

  const loadCategories = useCallback(async () => {
    const res = await planningApi.listCategories();
    setCategories(res.data || []);
  }, []);

  useEffect(() => {
    if (!loading) { loadActivities(); loadSummary(); }
  }, [loading, loadActivities, loadSummary]);

  useEffect(() => {
    if (activities.length > 0 && !ganttStart && !ganttEnd) {
      const starts = activities.map(a => a.planned_start).filter(Boolean).sort();
      const ends = activities.map(a => a.planned_end).filter(Boolean).sort();
      if (starts.length && ends.length) {
        const s = new Date(starts[0]); s.setDate(s.getDate() - 2);
        const e = new Date(ends[ends.length - 1]); e.setDate(e.getDate() + 2);
        setGanttStart(s.toISOString().split('T')[0]);
        setGanttEnd(e.toISOString().split('T')[0]);
      }
    }
  }, [activities, ganttStart, ganttEnd]);

  const subCategoriesForFilter = useMemo(() => {
    if (!filterCategory) return [];
    const cat = categories.find(c => c.name === filterCategory);
    return cat?.sub_categories || [];
  }, [filterCategory, categories]);

  const clearFilters = () => {
    setFilterProject(''); setFilterCategory(''); setFilterSubCategory('');
    setFilterStatus(''); setFilterPriority(''); setFilterApproval('');
    setFilterDateFrom(''); setFilterDateTo('');
  };

  const activeFilterCount = [filterProject, filterCategory, filterSubCategory, filterStatus, filterPriority, filterApproval, filterDateFrom, filterDateTo].filter(Boolean).length;

  // Inline patch handler
  const handleInlinePatch = useCallback(async (actId, field, value) => {
    await planningApi.patchActivity(actId, { [field]: value });
    loadActivities();
    loadSummary();
  }, [loadActivities, loadSummary]);

  // Gantt drag update
  const handleGanttDragUpdate = useCallback(async (actId, newStart, newEnd) => {
    await planningApi.patchActivity(actId, { planned_start: newStart, planned_end: newEnd });
    loadActivities();
    loadSummary();
  }, [loadActivities, loadSummary]);

  // Compute dependencies array for Gantt
  const ganttDeps = useMemo(() => {
    const deps = [];
    activities.forEach(a => {
      (a.dependencies || []).forEach(depId => {
        deps.push({ from: depId, to: a.id });
      });
    });
    return deps;
  }, [activities]);

  // ============ PDF EXPORT ============
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
    const pw = doc.internal.pageSize.getWidth();
    const companyName = companySettings?.company_name || 'ProjectBook Pro';
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pw, 32, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName, 14, 14);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('PROJECT PLANNING SCHEDULE', 14, 22);
    doc.setFontSize(8);
    doc.text(`Date: ${dateStr}`, 14, 28);

    doc.setFontSize(8);
    const ref = `REF: PP-${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
    doc.text(ref, pw - 14, 10, { align: 'right' });
    doc.text(`Revision: 0`, pw - 14, 16, { align: 'right' });
    doc.text(`Prepared by: Admin`, pw - 14, 22, { align: 'right' });
    doc.text(`Approved by: _______________`, pw - 14, 28, { align: 'right' });

    doc.setFillColor(30, 41, 59);
    doc.rect(0, 32, pw, 8, 'F');
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(7);
    const summaryText = `Total: ${activities.length} | Completed: ${activities.filter(a => a.status==='Completed').length} | In Progress: ${activities.filter(a => a.status==='In Progress').length} | Delayed: ${activities.filter(a => a.status==='Delayed').length} | Overdue: ${summary?.overdue_count || 0}`;
    doc.text(summaryText, 14, 37);

    const tableData = activities.map((a, i) => [
      i + 1, a.title, a.category, a.sub_category || '-',
      a.planned_start || '-', a.planned_end || '-', a.actual_start || '-', a.actual_end || '-',
      a.assignee || '-', a.priority, a.status, `${a.progress || 0}%`, a.approval_status,
    ]);

    autoTable(doc, {
      startY: 43,
      head: [['#', 'Activity', 'Category', 'Sub-Category', 'Plan Start', 'Plan End', 'Act. Start', 'Act. End', 'Assignee', 'Priority', 'Status', 'Progress', 'Approval']],
      body: tableData, theme: 'grid',
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [241, 245, 249] },
      columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 35 }, 10: { cellWidth: 18 } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 10) {
          const st = data.cell.raw;
          if (st === 'Completed') data.cell.styles.textColor = [6, 95, 70];
          else if (st === 'Delayed') data.cell.styles.textColor = [154, 52, 18];
          else if (st === 'Cancelled') data.cell.styles.textColor = [153, 27, 27];
          else if (st === 'In Progress') data.cell.styles.textColor = [30, 64, 175];
        }
      },
      margin: { left: 10, right: 10 },
    });

    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      const ph = doc.internal.pageSize.getHeight();
      doc.setFillColor(15, 23, 42);
      doc.rect(0, ph - 10, pw, 10, 'F');
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(7);
      doc.text(`${companyName} - Project Planning Schedule`, 14, ph - 4);
      doc.text(`Page ${i} of ${pages}`, pw - 14, ph - 4, { align: 'right' });
    }
    doc.save(`Planning_Schedule_${now.toISOString().split('T')[0]}.pdf`);
  };

  // ============ RENDER ============

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner /> <span className="ml-3 text-[var(--text-secondary)]">Loading planning module...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planning-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Project Planning</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Schedule, track, and approve project activities</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {aiEnabled && (
            <Button data-testid="ai-assistant-btn" onClick={() => setShowAiPanel(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-700 hover:to-violet-700">
              <Sparkle size={16} weight="fill" /> AI Assistant
            </Button>
          )}
          <Button data-testid="import-csv-btn" onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 bg-[var(--surface)] border border-[var(--border)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]">
            <UploadSimple size={16} /> Import CSV
          </Button>
          <Button data-testid="export-pdf-btn" onClick={exportPDF}
            className="flex items-center gap-2 bg-[var(--surface)] border border-[var(--border)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]">
            <Export size={16} /> Export PDF
          </Button>
          <Button data-testid="add-activity-btn" onClick={() => { setSelectedActivity(null); setShowCreateModal(true); }}
            className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700">
            <Plus size={16} /> Add Activity
          </Button>
        </div>
      </div>

      <div className="flex gap-1 p-1 rounded-lg bg-[var(--surface)] border border-[var(--border)] w-fit" data-testid="planning-tabs">
        {MAIN_TABS.map(t => {
          const Icon = t.icon;
          const active = mainTab === t.key;
          return (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setMainTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${active ? 'bg-blue-600 text-white shadow-md' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]'}`}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {(mainTab === 'schedule' || mainTab === 'gantt') && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <button data-testid="toggle-filters-btn" onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
              <Funnel size={16} />
              Filters {activeFilterCount > 0 && <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>}
              {showFilters ? <CaretDown size={12} /> : <CaretRight size={12} />}
            </button>
            {activeFilterCount > 0 && (
              <button data-testid="clear-filters-btn" onClick={clearFilters} className="text-xs text-blue-500 hover:underline">Clear all</button>
            )}
          </div>
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              <Select data-testid="filter-project" value={filterProject} onChange={e => setFilterProject(e.target.value)}>
                <option value="">All Projects</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
              <Select data-testid="filter-category" value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setFilterSubCategory(''); }}>
                <option value="">All Categories</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </Select>
              <Select data-testid="filter-sub-category" value={filterSubCategory} onChange={e => setFilterSubCategory(e.target.value)} disabled={!filterCategory}>
                <option value="">All Sub-Categories</option>
                {subCategoriesForFilter.map(sc => <option key={sc} value={sc}>{sc}</option>)}
              </Select>
              <Select data-testid="filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All Statuses</option>
                {meta.statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
              <Select data-testid="filter-priority" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
                <option value="">All Priorities</option>
                {meta.priorities.map(p => <option key={p} value={p}>{p}</option>)}
              </Select>
              <Select data-testid="filter-approval" value={filterApproval} onChange={e => setFilterApproval(e.target.value)}>
                <option value="">All Approvals</option>
                {meta.approval_statuses.map(a => <option key={a} value={a}>{a}</option>)}
              </Select>
              <div>
                <label className="text-xs text-[var(--text-secondary)] mb-1 block">Date From</label>
                <Input data-testid="filter-date-from" type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-[var(--text-secondary)] mb-1 block">Date To</label>
                <Input data-testid="filter-date-to" type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
              </div>
            </div>
          )}
        </Card>
      )}

      {mainTab === 'dashboard' && <DashboardTab summary={summary} />}
      {mainTab === 'schedule' && (
        <ScheduleTab
          activities={activities} meta={meta}
          onView={a => { setSelectedActivity(a); setShowDetailModal(true); }}
          onEdit={a => { setSelectedActivity(a); setShowCreateModal(true); }}
          onDelete={async id => { await planningApi.deleteActivity(id); loadActivities(); loadSummary(); }}
          onApprove={a => { setSelectedActivity(a); setShowApprovalModal(true); }}
          onInlinePatch={handleInlinePatch}
          onDeps={a => { setSelectedActivity(a); setShowDepsModal(true); }}
        />
      )}
      {mainTab === 'gantt' && (
        <div className="space-y-4">
          <Card className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <label className="text-xs text-[var(--text-secondary)] block mb-1">Gantt Start</label>
                <Input data-testid="gantt-start" type="date" value={ganttStart} onChange={e => setGanttStart(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-[var(--text-secondary)] block mb-1">Gantt End</label>
                <Input data-testid="gantt-end" type="date" value={ganttEnd} onChange={e => setGanttEnd(e.target.value)} />
              </div>
              <div className="flex items-end gap-3 mt-auto">
                <div className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                  <div className="w-3 h-3 rounded bg-blue-500 opacity-85" /> Planned
                </div>
                <div className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                  <div className="w-3 h-3 rounded bg-blue-500 opacity-45" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.3) 2px, rgba(255,255,255,0.3) 4px)' }} /> Actual
                </div>
                <div className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                  <div className="w-px h-3 bg-red-500" /> Today
                </div>
                <div className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                  <ArrowBendDownRight size={12} className="text-yellow-500" /> Dependency
                </div>
              </div>
            </div>
          </Card>
          <GanttChart activities={activities} startDate={ganttStart} endDate={ganttEnd}
            onUpdateDates={handleGanttDragUpdate} dependencies={ganttDeps} />
        </div>
      )}
      {mainTab === 'categories' && (
        <CategoriesTab categories={categories} onRefresh={loadCategories}
          onEdit={c => { setEditingCategory(c); setShowCategoryModal(true); }}
          onAdd={() => { setEditingCategory(null); setShowCategoryModal(true); }}
        />
      )}

      {/* Modals */}
      {showCreateModal && (
        <ActivityFormModal activity={selectedActivity} projects={projects} categories={categories} meta={meta}
          allActivities={activities}
          onClose={() => setShowCreateModal(false)}
          onSave={async data => {
            if (selectedActivity) await planningApi.updateActivity(selectedActivity.id, data);
            else await planningApi.createActivity(data);
            setShowCreateModal(false); loadActivities(); loadSummary();
          }}
        />
      )}
      {showDetailModal && selectedActivity && (
        <ActivityDetailModal activity={selectedActivity} onClose={() => setShowDetailModal(false)} />
      )}
      {showApprovalModal && selectedActivity && (
        <ApprovalModal activity={selectedActivity} meta={meta}
          onClose={() => setShowApprovalModal(false)}
          onSave={async data => { await planningApi.approveActivity(selectedActivity.id, data); setShowApprovalModal(false); loadActivities(); loadSummary(); }}
        />
      )}
      {showCategoryModal && (
        <CategoryFormModal category={editingCategory}
          onClose={() => setShowCategoryModal(false)}
          onSave={async data => {
            if (editingCategory) await planningApi.updateCategory(editingCategory.id, data);
            else await planningApi.createCategory(data);
            setShowCategoryModal(false); loadCategories();
          }}
        />
      )}
      {showImportModal && (
        <CSVImportModal onClose={() => setShowImportModal(false)}
          onImported={() => { setShowImportModal(false); loadActivities(); loadSummary(); }}
        />
      )}
      {showDepsModal && selectedActivity && (
        <DependencyModal activity={selectedActivity} allActivities={activities}
          onClose={() => setShowDepsModal(false)}
          onSave={async deps => { await planningApi.setDependencies(selectedActivity.id, { dependencies: deps }); setShowDepsModal(false); loadActivities(); }}
        />
      )}
      {showAiPanel && aiEnabled && (
        <AIPlanningPanel
          activities={activities}
          onClose={() => setShowAiPanel(false)}
          onActivitiesGenerated={(newActs) => { setShowAiPanel(false); loadActivities(); loadSummary(); }}
        />
      )}
    </div>
  );
}

// ============ DASHBOARD TAB ============

function DashboardTab({ summary }) {
  if (!summary) return <Spinner />;

  const statusData = Object.entries(summary.by_status).map(([name, value]) => ({ name, value, fill: STATUS_COLORS[name]?.fill || '#6B7280' }));
  const priorityData = Object.entries(summary.by_priority).map(([name, value]) => ({ name, value, fill: PRIORITY_COLORS[name] || '#6B7280' }));
  const categoryData = Object.entries(summary.by_category).map(([name, value], i) => ({ name, value, fill: CHART_COLORS[i % CHART_COLORS.length] }));
  const approvalData = Object.entries(summary.by_approval).map(([name, value]) => ({ name, value, fill: APPROVAL_COLORS[name] || '#6B7280' }));

  const kpiCards = [
    { label: 'Total Activities', value: summary.total, icon: ListBullets, color: '#3B82F6' },
    { label: 'Milestones', value: summary.milestones, icon: Flag, color: '#8B5CF6' },
    { label: 'Completed', value: summary.by_status?.['Completed'] || 0, icon: CheckCircle, color: '#10B981' },
    { label: 'In Progress', value: summary.by_status?.['In Progress'] || 0, icon: Timer, color: '#3B82F6' },
    { label: 'Delayed', value: summary.by_status?.['Delayed'] || 0, icon: WarningDiamond, color: '#F97316' },
    { label: 'Overdue', value: summary.overdue_count, icon: WarningCircle, color: '#EF4444' },
  ];

  return (
    <div className="space-y-6" data-testid="planning-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpiCards.map((k, i) => {
          const Icon = k.icon;
          return (
            <Card key={i} className="p-4" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[var(--text-secondary)]">{k.label}</p>
                  <p className="text-2xl font-bold mt-1" style={{ color: k.color }}>{k.value}</p>
                </div>
                <Icon size={28} weight="duotone" style={{ color: k.color, opacity: 0.4 }} />
              </div>
            </Card>
          );
        })}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { title: 'By Status', data: statusData },
          { title: 'By Priority', data: priorityData },
        ].map(({ title, data }) => (
          <Card key={title} className="p-4">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">{title}</h3>
            {data.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart><Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                  {data.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-[var(--text-muted)] text-center py-8">No data</p>}
          </Card>
        ))}
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">By Category</h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={categoryData}><XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} /><YAxis tick={{ fontSize: 10 }} /><Tooltip />
                <Bar dataKey="value" radius={[4,4,0,0]}>{categoryData.map((e,i) => <Cell key={i} fill={e.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-[var(--text-muted)] text-center py-8">No data</p>}
        </Card>
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Approval Status</h3>
          {approvalData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart><Pie data={approvalData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                {approvalData.map((e,i) => <Cell key={i} fill={e.fill} />)}
              </Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-[var(--text-muted)] text-center py-8">No data</p>}
        </Card>
      </div>
      {summary.overdue?.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-red-500 mb-3 flex items-center gap-2"><WarningCircle size={16} weight="fill" /> Overdue Activities</h3>
          <div className="space-y-2">
            {summary.overdue.map(o => (
              <div key={o.id} className="flex items-center justify-between p-2 rounded bg-red-500/5 border border-red-500/20">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-[var(--text-primary)]">{o.title}</span>
                  <span className="text-xs text-[var(--text-secondary)]">{o.category}</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-[var(--text-secondary)]">Due: {o.planned_end}</span>
                  <span className="text-[var(--text-secondary)]">{o.assignee}</span>
                  <StatusBadge status={o.status} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ============ SCHEDULE TAB WITH INLINE EDITING ============

function ScheduleTab({ activities, meta, onView, onEdit, onDelete, onApprove, onInlinePatch, onDeps }) {
  if (activities.length === 0) {
    return (
      <Card className="p-12 text-center">
        <CalendarBlank size={48} className="mx-auto text-[var(--text-muted)] mb-3" />
        <p className="text-[var(--text-secondary)]">No activities found. Create your first activity to get started.</p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden" data-testid="schedule-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--surface)] border-b border-[var(--border)]">
              {['#', 'Activity', 'Category', 'Plan Start', 'Plan End', 'Assignee', 'Priority', 'Status', 'Progress', 'Approval', 'Actions'].map(h => (
                <th key={h} className="text-left px-3 py-3 text-xs font-semibold text-[var(--text-secondary)] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activities.map((a, i) => (
              <tr key={a.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-hover)] transition-colors" data-testid={`activity-row-${a.id}`}>
                <td className="px-3 py-2.5 text-[var(--text-muted)] text-xs">{i + 1}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    {a.milestone && <Flag weight="fill" size={14} className="text-yellow-500" />}
                    <div>
                      <p className="font-medium text-[var(--text-primary)]">{a.title}</p>
                      {a.sub_category && <p className="text-xs text-[var(--text-muted)]">{a.sub_category}</p>}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-xs text-[var(--text-secondary)]">{a.category}</td>
                <td className="px-3 py-2.5" data-testid={`inline-start-${a.id}`}>
                  <InlineInput value={a.planned_start} type="date" onSave={v => onInlinePatch(a.id, 'planned_start', v)} />
                </td>
                <td className="px-3 py-2.5" data-testid={`inline-end-${a.id}`}>
                  <InlineInput value={a.planned_end} type="date" onSave={v => onInlinePatch(a.id, 'planned_end', v)} />
                </td>
                <td className="px-3 py-2.5" data-testid={`inline-assignee-${a.id}`}>
                  <InlineInput value={a.assignee || ''} onSave={v => onInlinePatch(a.id, 'assignee', v)} />
                </td>
                <td className="px-3 py-2.5" data-testid={`inline-priority-${a.id}`}>
                  <InlineSelect value={a.priority} options={ALL_PRIORITIES} onSave={v => onInlinePatch(a.id, 'priority', v)}
                    renderValue={v => <PriorityBadge priority={v} />} />
                </td>
                <td className="px-3 py-2.5" data-testid={`inline-status-${a.id}`}>
                  <InlineSelect value={a.status} options={ALL_STATUSES} onSave={v => onInlinePatch(a.id, 'status', v)}
                    renderValue={v => <StatusBadge status={v} />} />
                </td>
                <td className="px-3 py-2.5" data-testid={`inline-progress-${a.id}`}>
                  <InlineProgress value={a.progress || 0} onSave={v => onInlinePatch(a.id, 'progress', v)} />
                </td>
                <td className="px-3 py-2.5"><ApprovalBadge status={a.approval_status} /></td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    <button data-testid={`view-activity-${a.id}`} onClick={() => onView(a)} className="p-1.5 rounded hover:bg-blue-500/10 text-blue-500" title="View"><Eye size={15} /></button>
                    <button data-testid={`edit-activity-${a.id}`} onClick={() => onEdit(a)} className="p-1.5 rounded hover:bg-yellow-500/10 text-yellow-500" title="Edit"><PencilSimple size={15} /></button>
                    <button data-testid={`approve-activity-${a.id}`} onClick={() => onApprove(a)} className="p-1.5 rounded hover:bg-green-500/10 text-green-500" title="Approve"><Stamp size={15} /></button>
                    <button data-testid={`deps-activity-${a.id}`} onClick={() => onDeps(a)} className="p-1.5 rounded hover:bg-purple-500/10 text-purple-500" title="Dependencies"><LinkIcon size={15} /></button>
                    <button data-testid={`delete-activity-${a.id}`} onClick={() => { if (window.confirm('Delete this activity?')) onDelete(a.id); }} className="p-1.5 rounded hover:bg-red-500/10 text-red-500" title="Delete"><Trash size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ============ CATEGORIES TAB ============

function CategoriesTab({ categories, onRefresh, onEdit, onAdd }) {
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    await planningApi.deleteCategory(id);
    onRefresh();
  };
  return (
    <div className="space-y-4" data-testid="categories-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-[var(--text-primary)]">Activity Categories & Sub-Categories</h3>
        <Button data-testid="add-category-btn" onClick={onAdd} className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700">
          <Plus size={16} /> Add Category
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map(cat => (
          <Card key={cat.id} className="p-4" data-testid={`category-card-${cat.id}`}>
            <div className="flex items-start justify-between mb-3">
              <h4 className="font-semibold text-[var(--text-primary)]">{cat.name}</h4>
              <div className="flex gap-1">
                <button data-testid={`edit-category-${cat.id}`} onClick={() => onEdit(cat)} className="p-1.5 rounded hover:bg-yellow-500/10 text-yellow-500"><PencilSimple size={15} /></button>
                <button data-testid={`delete-category-${cat.id}`} onClick={() => handleDelete(cat.id)} className="p-1.5 rounded hover:bg-red-500/10 text-red-500"><Trash size={15} /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(cat.sub_categories || []).map((sc, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded bg-[var(--surface-hover)] text-[var(--text-secondary)] border border-[var(--border)]">{sc}</span>
              ))}
              {(!cat.sub_categories || cat.sub_categories.length === 0) && <span className="text-xs text-[var(--text-muted)]">No sub-categories</span>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============ ACTIVITY FORM MODAL ============

function ActivityFormModal({ activity, projects, categories, meta, allActivities, onClose, onSave }) {
  const [form, setForm] = useState({
    title: activity?.title || '', description: activity?.description || '',
    project_id: activity?.project_id || '', project_name: activity?.project_name || '',
    category: activity?.category || '', sub_category: activity?.sub_category || '',
    planned_start: activity?.planned_start || '', planned_end: activity?.planned_end || '',
    actual_start: activity?.actual_start || '', actual_end: activity?.actual_end || '',
    assignee: activity?.assignee || '', priority: activity?.priority || 'Medium',
    status: activity?.status || 'Not Started', progress: activity?.progress || 0,
    milestone: activity?.milestone || false, notes: activity?.notes || '',
    revision: activity?.revision || 0,
  });
  const [saving, setSaving] = useState(false);

  const subCategories = useMemo(() => {
    if (!form.category) return [];
    const cat = categories.find(c => c.name === form.category);
    return cat?.sub_categories || [];
  }, [form.category, categories]);

  const handleProject = (e) => {
    const pid = e.target.value;
    const proj = projects.find(p => p.id === pid);
    setForm(f => ({ ...f, project_id: pid, project_name: proj?.name || '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={activity ? 'Edit Activity' : 'New Activity'}>
      <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="activity-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Title *</label>
            <Input data-testid="input-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Description</label>
            <textarea data-testid="input-description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] text-[var(--text-primary)] px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" rows={2} />
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Project</label>
            <Select data-testid="select-project" value={form.project_id} onChange={handleProject}>
              <option value="">No Project</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Category *</label>
            <Select data-testid="select-category" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value, sub_category: '' }))} required>
              <option value="">Select Category</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Sub-Category</label>
            <Select data-testid="select-sub-category" value={form.sub_category} onChange={e => setForm(f => ({ ...f, sub_category: e.target.value }))}>
              <option value="">Select Sub-Category</option>
              {subCategories.map(sc => <option key={sc} value={sc}>{sc}</option>)}
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Assignee</label>
            <Input data-testid="input-assignee" value={form.assignee} onChange={e => setForm(f => ({ ...f, assignee: e.target.value }))} placeholder="Person responsible" />
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Planned Start *</label>
            <Input data-testid="input-planned-start" type="date" value={form.planned_start} onChange={e => setForm(f => ({ ...f, planned_start: e.target.value }))} required />
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Planned End *</label>
            <Input data-testid="input-planned-end" type="date" value={form.planned_end} onChange={e => setForm(f => ({ ...f, planned_end: e.target.value }))} required />
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Actual Start</label>
            <Input data-testid="input-actual-start" type="date" value={form.actual_start} onChange={e => setForm(f => ({ ...f, actual_start: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Actual End</label>
            <Input data-testid="input-actual-end" type="date" value={form.actual_end} onChange={e => setForm(f => ({ ...f, actual_end: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Priority</label>
            <Select data-testid="select-priority" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
              {meta.priorities.map(p => <option key={p} value={p}>{p}</option>)}
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Status</label>
            <Select data-testid="select-status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              {meta.statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Progress (%)</label>
            <div className="flex items-center gap-3">
              <input data-testid="input-progress" type="range" min="0" max="100" step="5" value={form.progress} onChange={e => setForm(f => ({ ...f, progress: parseInt(e.target.value) }))} className="flex-1 accent-blue-600" />
              <span className="text-sm font-semibold text-[var(--text-primary)] w-10 text-right">{form.progress}%</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Revision</label>
            <Input data-testid="input-revision" type="number" min="0" value={form.revision} onChange={e => setForm(f => ({ ...f, revision: parseInt(e.target.value) || 0 }))} />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <input data-testid="input-milestone" type="checkbox" checked={form.milestone} onChange={e => setForm(f => ({ ...f, milestone: e.target.checked }))} className="rounded border-[var(--border)] text-blue-600" />
            <label className="text-sm text-[var(--text-primary)]">Mark as Milestone</label>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Notes</label>
            <textarea data-testid="input-notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] text-[var(--text-primary)] px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" rows={2} />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" onClick={onClose} className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]">Cancel</Button>
          <Button data-testid="save-activity-btn" type="submit" disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700">
            {saving ? 'Saving...' : (activity ? 'Update Activity' : 'Create Activity')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ============ DETAIL MODAL ============

function ActivityDetailModal({ activity, onClose }) {
  const a = activity;
  const fields = [
    ['Title', a.title], ['Description', a.description || '-'],
    ['Project', a.project_name || '-'], ['Category', a.category],
    ['Sub-Category', a.sub_category || '-'], ['Assignee', a.assignee || '-'],
    ['Planned Start', a.planned_start], ['Planned End', a.planned_end],
    ['Actual Start', a.actual_start || '-'], ['Actual End', a.actual_end || '-'],
    ['Priority', a.priority], ['Status', a.status],
    ['Progress', `${a.progress || 0}%`], ['Milestone', a.milestone ? 'Yes' : 'No'],
    ['Approval', a.approval_status], ['Approved By', a.approved_by || '-'],
    ['Approval Date', a.approval_date || '-'], ['Approval Notes', a.approval_notes || '-'],
    ['Revision', a.revision || 0], ['Prepared By', a.prepared_by || '-'],
    ['Dependencies', (a.dependencies || []).length > 0 ? `${a.dependencies.length} linked` : 'None'],
    ['Notes', a.notes || '-'],
  ];
  return (
    <Modal open onClose={onClose} title="Activity Details">
      <div className="space-y-3 max-h-[65vh] overflow-y-auto" data-testid="activity-detail">
        {fields.map(([label, val], i) => (
          <div key={i} className="flex items-start gap-3">
            <span className="text-xs font-semibold text-[var(--text-secondary)] w-32 shrink-0">{label}</span>
            <span className="text-sm text-[var(--text-primary)]">{val}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ============ APPROVAL MODAL ============

function ApprovalModal({ activity, meta, onClose, onSave }) {
  const [status, setStatus] = useState(activity.approval_status || 'Pending');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  return (
    <Modal open onClose={onClose} title={`Approve: ${activity.title}`}>
      <div className="space-y-4" data-testid="approval-form">
        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Approval Decision</label>
          <Select data-testid="select-approval-status" value={status} onChange={e => setStatus(e.target.value)}>
            {meta.approval_statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Notes</label>
          <textarea data-testid="input-approval-notes" value={notes} onChange={e => setNotes(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] text-[var(--text-primary)] px-3 py-2 text-sm" rows={3} placeholder="Approval notes..." />
        </div>
        <div className="flex justify-end gap-3">
          <Button type="button" onClick={onClose} className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text-primary)]">Cancel</Button>
          <Button data-testid="submit-approval-btn" disabled={saving}
            onClick={async () => { setSaving(true); try { await onSave({ approval_status: status, notes }); } finally { setSaving(false); } }}
            className="bg-green-600 text-white hover:bg-green-700">
            {saving ? 'Saving...' : 'Submit Decision'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ============ CATEGORY FORM MODAL ============

function CategoryFormModal({ category, onClose, onSave }) {
  const [name, setName] = useState(category?.name || '');
  const [subCats, setSubCats] = useState((category?.sub_categories || []).join(', '));
  const [saving, setSaving] = useState(false);
  return (
    <Modal open onClose={onClose} title={category ? 'Edit Category' : 'New Category'}>
      <div className="space-y-4" data-testid="category-form">
        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Category Name *</label>
          <Input data-testid="input-category-name" value={name} onChange={e => setName(e.target.value)} required />
        </div>
        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Sub-Categories (comma separated)</label>
          <textarea data-testid="input-sub-categories" value={subCats} onChange={e => setSubCats(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] text-[var(--text-primary)] px-3 py-2 text-sm" rows={3} placeholder="e.g., Excavation, Foundation, Concrete Works" />
        </div>
        <div className="flex justify-end gap-3">
          <Button type="button" onClick={onClose} className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text-primary)]">Cancel</Button>
          <Button data-testid="save-category-btn" disabled={saving}
            onClick={async () => { setSaving(true); try { const parsed = subCats.split(',').map(s => s.trim()).filter(Boolean); await onSave({ name: name.trim(), sub_categories: parsed }); } finally { setSaving(false); } }}
            className="bg-blue-600 text-white hover:bg-blue-700">
            {saving ? 'Saving...' : (category ? 'Update' : 'Create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ============ CSV IMPORT MODAL ============

function CSVImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await planningApi.importCSV(formData);
      setResult(res.data);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Import failed' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Import Activities from CSV">
      <div className="space-y-4" data-testid="csv-import-modal">
        {!result ? (
          <>
            <div className="border-2 border-dashed border-[var(--border)] rounded-lg p-6 text-center hover:border-blue-500/50 transition-colors">
              <UploadSimple size={32} className="mx-auto text-[var(--text-muted)] mb-2" />
              <p className="text-sm text-[var(--text-secondary)] mb-2">
                Drop a CSV file or click to browse
              </p>
              <input ref={fileRef} type="file" accept=".csv" onChange={e => setFile(e.target.files[0])}
                className="hidden" data-testid="csv-file-input" />
              <Button onClick={() => fileRef.current?.click()}
                className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text-primary)] text-sm">
                Choose File
              </Button>
              {file && <p className="text-xs text-blue-500 mt-2">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
            </div>

            <div className="bg-[var(--surface)] rounded-lg p-3 border border-[var(--border)]">
              <h4 className="text-xs font-semibold text-[var(--text-primary)] mb-2">Expected CSV Columns</h4>
              <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                <strong>Required:</strong> Title (or Activity)<br />
                <strong>Optional:</strong> Category, Sub Category, Start Date, End Date, Assignee, Priority, Status, Progress, Milestone, Notes
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button onClick={onClose} className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text-primary)]">Cancel</Button>
              <Button data-testid="upload-csv-btn" onClick={handleUpload} disabled={!file || uploading}
                className="bg-blue-600 text-white hover:bg-blue-700">
                {uploading ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </>
        ) : (
          <>
            {result.error ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                <XCircle size={32} weight="fill" className="mx-auto text-red-500 mb-2" />
                <p className="text-sm text-red-500">{result.error}</p>
              </div>
            ) : (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                <CheckCircle size={32} weight="fill" className="mx-auto text-green-500 mb-2" />
                <p className="text-sm text-green-600 font-semibold">Import Complete</p>
                <div className="mt-2 text-xs text-[var(--text-secondary)] space-y-1">
                  <p><strong>{result.created}</strong> activities created</p>
                  {result.skipped > 0 && <p><strong>{result.skipped}</strong> rows skipped (no title)</p>}
                  {result.errors?.length > 0 && <p className="text-red-500"><strong>{result.errors.length}</strong> errors</p>}
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <Button data-testid="csv-import-done-btn" onClick={result.error ? onClose : onImported}
                className="bg-blue-600 text-white hover:bg-blue-700">
                {result.error ? 'Close' : 'Done'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ============ DEPENDENCY MODAL ============

function DependencyModal({ activity, allActivities, onClose, onSave }) {
  const [deps, setDeps] = useState(activity.dependencies || []);
  const [saving, setSaving] = useState(false);
  const available = allActivities.filter(a => a.id !== activity.id && !deps.includes(a.id));

  return (
    <Modal open onClose={onClose} title={`Dependencies: ${activity.title}`}>
      <div className="space-y-4" data-testid="dependency-modal">
        <p className="text-xs text-[var(--text-secondary)]">
          Select activities that must be completed <strong>before</strong> this activity can start.
        </p>

        {deps.length > 0 && (
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[var(--text-primary)]">Current Dependencies</label>
            {deps.map(depId => {
              const depAct = allActivities.find(a => a.id === depId);
              return (
                <div key={depId} className="flex items-center justify-between p-2 rounded bg-[var(--surface)] border border-[var(--border)]">
                  <div className="flex items-center gap-2">
                    <ArrowBendDownRight size={14} className="text-yellow-500" />
                    <span className="text-sm text-[var(--text-primary)]">{depAct?.title || depId}</span>
                  </div>
                  <button onClick={() => setDeps(d => d.filter(id => id !== depId))}
                    className="p-1 rounded hover:bg-red-500/10 text-red-500"><Trash size={14} /></button>
                </div>
              );
            })}
          </div>
        )}

        {available.length > 0 && (
          <div>
            <label className="text-xs font-semibold text-[var(--text-primary)] block mb-1">Add Dependency</label>
            <select onChange={e => { if (e.target.value) { setDeps(d => [...d, e.target.value]); e.target.value = ''; } }}
              className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] text-[var(--text-primary)] px-3 py-2 text-sm"
              data-testid="add-dependency-select">
              <option value="">Select activity...</option>
              {available.map(a => <option key={a.id} value={a.id}>{a.title} ({a.category})</option>)}
            </select>
          </div>
        )}

        {deps.length === 0 && available.length === 0 && (
          <p className="text-sm text-[var(--text-muted)] text-center py-4">No other activities to link.</p>
        )}

        <div className="flex justify-end gap-3">
          <Button onClick={onClose} className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text-primary)]">Cancel</Button>
          <Button data-testid="save-deps-btn" disabled={saving}
            onClick={async () => { setSaving(true); try { await onSave(deps); } finally { setSaving(false); } }}
            className="bg-blue-600 text-white hover:bg-blue-700">
            {saving ? 'Saving...' : 'Save Dependencies'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}


// ============ AI PLANNING PANEL ============

function AIPlanningPanel({ activities, onClose, onActivitiesGenerated }) {
  const [activeTab, setActiveTab] = useState('generate');
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [riskResult, setRiskResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true); setResult(null);
    try {
      const res = await aiApi.generateSchedule({ prompt: prompt.trim() });
      setResult(res.data);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Generation failed' });
    } finally { setLoading(false); }
  };

  const handleRiskAnalysis = async () => {
    if (!activities.length) return;
    setLoading(true); setRiskResult(null);
    try {
      const res = await aiApi.riskAnalysis({ activities });
      setRiskResult(res.data);
    } catch (err) {
      setRiskResult({ error: err.response?.data?.detail || 'Analysis failed' });
    } finally { setLoading(false); }
  };

  const handleImportGenerated = async () => {
    if (!result?.activities?.length) return;
    setImporting(true);
    try {
      for (const act of result.activities) {
        await planningApi.createActivity({
          title: act.title || '',
          category: act.category || '',
          sub_category: act.sub_category || '',
          planned_start: act.planned_start || '',
          planned_end: act.planned_end || '',
          assignee: act.assignee || '',
          priority: act.priority || 'Medium',
          notes: act.notes || '',
          milestone: act.milestone || false,
          status: 'Not Started',
        });
      }
      onActivitiesGenerated(result.activities);
    } catch (err) {
      alert('Failed to import some activities');
    } finally { setImporting(false); }
  };

  const RISK_COLORS = { Low: '#10B981', Medium: '#F59E0B', High: '#F97316', Critical: '#EF4444' };

  return (
    <Modal open onClose={onClose} title="AI Planning Assistant">
      <div className="space-y-4 max-h-[70vh] overflow-y-auto" data-testid="ai-planning-panel">
        {/* Sub-tabs */}
        <div className="flex gap-1 p-0.5 rounded-lg bg-[var(--surface)] border border-[var(--border)] w-fit">
          {[
            { key: 'generate', label: 'Generate Schedule', icon: Sparkle },
            { key: 'risk', label: 'Risk Analysis', icon: WarningOctagon },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === t.key ? 'bg-blue-600 text-white' : 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]'}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {/* Generate Schedule */}
        {activeTab === 'generate' && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">Describe what you need</label>
              <textarea data-testid="ai-generate-prompt" value={prompt} onChange={e => setPrompt(e.target.value)}
                className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] text-[var(--text-primary)] px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3} placeholder='e.g., "Generate a 3-month schedule for a villa construction project starting Jan 2027, including civil works, MEP, and finishing"' />
            </div>
            <Button data-testid="ai-generate-btn" onClick={handleGenerate} disabled={loading || !prompt.trim()}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-700 hover:to-violet-700 w-full justify-center">
              {loading ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
              {loading ? 'Generating...' : 'Generate Schedule'}
            </Button>

            {result && !result.error && (
              <div className="space-y-3 mt-3">
                {result.summary && (
                  <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                    <p className="text-xs font-semibold text-blue-600 mb-1">AI Summary</p>
                    <p className="text-sm text-[var(--text-primary)]">{result.summary}</p>
                    {result.total_duration_days && (
                      <p className="text-xs text-[var(--text-muted)] mt-1">Est. duration: {result.total_duration_days} days</p>
                    )}
                  </div>
                )}
                {result.activities?.length > 0 && (
                  <>
                    <p className="text-xs font-semibold text-[var(--text-primary)]">Generated {result.activities.length} activities:</p>
                    <div className="max-h-48 overflow-y-auto space-y-1.5 border border-[var(--border)] rounded-lg p-2">
                      {result.activities.map((a, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded bg-[var(--surface)] text-xs">
                          <div>
                            <span className="font-medium text-[var(--text-primary)]">{a.title}</span>
                            <span className="text-[var(--text-muted)] ml-2">{a.category}</span>
                          </div>
                          <span className="text-[var(--text-secondary)]">{a.planned_start} → {a.planned_end}</span>
                        </div>
                      ))}
                    </div>
                    <Button data-testid="ai-import-generated-btn" onClick={handleImportGenerated} disabled={importing}
                      className="flex items-center gap-2 bg-green-600 text-white hover:bg-green-700 w-full justify-center">
                      {importing ? <CircleNotch size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                      {importing ? 'Importing...' : `Import ${result.activities.length} Activities`}
                    </Button>
                  </>
                )}
              </div>
            )}
            {result?.error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-500">{result.error}</div>
            )}
          </div>
        )}

        {/* Risk Analysis */}
        {activeTab === 'risk' && (
          <div className="space-y-3">
            <p className="text-xs text-[var(--text-secondary)]">
              Analyze your current {activities.length} activities for schedule risks, delays, and improvement opportunities.
            </p>
            <Button data-testid="ai-risk-btn" onClick={handleRiskAnalysis} disabled={loading || !activities.length}
              className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 w-full justify-center">
              {loading ? <CircleNotch size={16} className="animate-spin" /> : <Brain size={16} weight="fill" />}
              {loading ? 'Analyzing...' : 'Run Risk Analysis'}
            </Button>

            {riskResult && !riskResult.error && (
              <div className="space-y-3 mt-2">
                <div className="flex items-center gap-2 p-3 rounded-lg border" style={{
                  borderColor: RISK_COLORS[riskResult.overall_risk_level] || '#6B7280',
                  backgroundColor: `${RISK_COLORS[riskResult.overall_risk_level] || '#6B7280'}10`,
                }}>
                  <WarningOctagon size={20} weight="fill" style={{ color: RISK_COLORS[riskResult.overall_risk_level] || '#6B7280' }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: RISK_COLORS[riskResult.overall_risk_level] }}>
                      Overall Risk: {riskResult.overall_risk_level}
                    </p>
                    {riskResult.schedule_health && (
                      <p className="text-xs text-[var(--text-secondary)]">{riskResult.schedule_health}</p>
                    )}
                  </div>
                </div>

                {riskResult.risks?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-[var(--text-primary)]">Identified Risks ({riskResult.risks.length})</p>
                    {riskResult.risks.map((r, i) => (
                      <div key={i} className="p-2.5 rounded-lg border border-[var(--border)] bg-[var(--surface)]">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold" style={{ color: RISK_COLORS[r.severity] || '#6B7280' }}>{r.severity}</span>
                          <span className="text-sm font-medium text-[var(--text-primary)]">{r.title}</span>
                        </div>
                        <p className="text-xs text-[var(--text-secondary)]">{r.description}</p>
                        {r.mitigation && (
                          <p className="text-xs text-green-600 mt-1"><strong>Mitigation:</strong> {r.mitigation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {riskResult.recommendations?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-[var(--text-primary)] mb-1">Recommendations</p>
                    <ul className="space-y-1">
                      {riskResult.recommendations.map((r, i) => (
                        <li key={i} className="text-xs text-[var(--text-secondary)] flex items-start gap-1.5">
                          <CheckCircle size={12} className="text-green-500 mt-0.5 flex-shrink-0" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {riskResult?.error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-500">{riskResult.error}</div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
